/**
 * translatedSelectionGuard.ts — PLAT-10467
 *
 * Selection-precedence guard shared by the right-click "Edit translation"
 * (`onContextMenu`) handlers on the translated surfaces (`AlignedBlocksView`,
 * `TranscriptionTextTranslated`, `TranscriptionDiffTranslated`).
 *
 * When a non-empty translated-column selection already covers the right-clicked
 * phrase, the span path (`useSpanReplace` + `SpanReplaceTrigger` /
 * `ReplaceSpanEditor`) owns the edit, so the single-word context menu must NOT
 * open and steal the gesture. The handlers call this; on `true` they
 * `preventDefault()` and return (selection wins), otherwise they fall through to
 * their existing single-word behavior.
 *
 * Reuses the span path's OWN resolver (`resolveTranslatedSelectionPhrases`,
 * scoped to the column ref — CLAUDE.md #12) so "what counts as a selection" is
 * the identical rule on both sides.
 */

import { resolveTranslatedSelectionPhrases } from '@src/stores-v2/resolveTranslatedSelectionPhrases';

import type { TranslationPhrase } from '@src/types.ts/translation';

/**
 * Whether `clickedPhrase` lies inside the current non-empty selection within
 * `container`. Resolves the selection with the span path's resolver (filtering
 * to the ACTIVE set the resolver expects), then tests membership against the
 * covered phrases: reference identity first (the resolver returns the same
 * phrase objects), then stable element-id overlap — robust to object reference
 * differences between the rendered phrase and the store's array.
 *
 * Returns false on any unresolved selection (collapsed caret, outside the
 * column, scattered run, or a click on a word outside the selection), so the
 * caller keeps its existing single-word behavior in every non-precedence case.
 */
export function isPhraseInTranslatedSelection(
  container: HTMLElement | null,
  allPhrases: readonly TranslationPhrase[] | undefined,
  clickedPhrase: TranslationPhrase,
): boolean {
  const activePhrases = (allPhrases ?? []).filter(p => p.active !== false);
  const resolution = resolveTranslatedSelectionPhrases(
    container,
    activePhrases,
  );
  if (!resolution.ok) return false;
  // Precedence-only — decides selection-vs-single-word, authors/drops nothing.
  if (resolution.phrases.includes(clickedPhrase)) return true;
  // Fall back to stable element-id overlap when the rendered phrase and the
  // store's array are different object references (PLAT-10611).
  if (!clickedPhrase.originalElementIds) return false;
  const coveredIds = new Set(
    resolution.phrases.flatMap(p => p.originalElementIds ?? []),
  );
  return clickedPhrase.originalElementIds.some(id => coveredIds.has(id));
}
